/**
 * OMI Swiper Slider
 * —————————————————
 * OOP carousel controller. Supports multiple independent instances
 * on the same page. Each <section data-omi-swiper-slider> gets its
 * own Swiper instance, tracked by section ID.
 *
 * Depends on Swiper 11+ (loaded globally or injected by the section).
 */

(function () {
  'use strict';

  /* ── Instance registry ─────────────────────────────────── */
  var instances = {};

  /* ── Class ─────────────────────────────────────────────── */
  function OmiSwiperSlider(sectionEl) {
    this.section     = sectionEl;
    this.id          = sectionEl.dataset.sectionId;
    this.swiperEl    = sectionEl.querySelector('[data-swiper]');
    this.pagination  = sectionEl.querySelector('[data-pagination]');
    this.liveRegion  = sectionEl.querySelector('[data-live-region]');
    this.swiper      = null;

    if (this.swiperEl) {
      this._init();
    }
  }

  OmiSwiperSlider.prototype._init = function () {
    var self       = this;
    var slideCount = this.swiperEl.querySelectorAll('.swiper-slide').length;

    /* Read dynamic config from data attributes */
    var spaceMobile  = parseInt(this.swiperEl.dataset.spaceMobile, 10)  || 12;
    var spaceDesktop = parseInt(this.swiperEl.dataset.spaceDesktop, 10) || 20;
    var breakpoint   = parseInt(this.swiperEl.dataset.breakpoint, 10)   || 750;
    var isAutoSlides = this.swiperEl.hasAttribute('data-slides-auto');

    /* Build breakpoints object dynamically */
    var bps = {};
    bps[breakpoint] = {
      slidesPerView    : isAutoSlides ? 'auto' : Math.min(slideCount, 3.15),
      spaceBetween     : spaceDesktop,
      slidesOffsetAfter: 32  /* match padding-inline var(--space-8) at 750px */
    };
    bps[1081] = {
      slidesPerView    : isAutoSlides ? 'auto' : Math.min(slideCount, 4.15),
      spaceBetween     : spaceDesktop,
      slidesOffsetAfter: 48  /* match padding-inline var(--space-12) at 1081px */
    };
    bps[1400] = {
      slidesPerView    : isAutoSlides ? 'auto' : Math.min(slideCount, 6),
      spaceBetween     : spaceDesktop,
      slidesOffsetAfter: 48
    };

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView    : isAutoSlides ? 'auto' : 1.2,
      spaceBetween     : spaceMobile,
      slidesOffsetAfter: 16,  /* match padding-inline-start var(--space-4) on mobile */
      watchOverflow    : true,
      grabCursor       : true,

      /* Keyboard — arrows move slides when in viewport */
      keyboard: {
        enabled        : true,
        onlyInViewport : true
      },

      /* Accessibility — Swiper's built-in a11y module */
      a11y: {
        enabled                       : true,
        containerRoleDescriptionMessage : 'carousel',
        itemRoleDescriptionMessage      : 'slide',
        paginationBulletMessage         : 'Go to slide {{index}}'
      },

      /* Pagination — <button> elements for keyboard access */
      pagination: {
        el            : self.pagination,
        clickable     : true,
        bulletElement : 'button'
      },

      /* Responsive breakpoints — built dynamically above */
      breakpoints: bps,

      /* Events */
      on: {
        slideChange: function () {
          self._announceSlide();
        },
        afterInit: function () {
          self._checkOverflow();
        },
        resize: function () {
          self._checkOverflow();
        },
        breakpoint: function () {
          self._checkOverflow();
        }
      }
    });
  };

  /**
   * Detect whether all slides fit inside the container.
   * When they do: remove trailing offset, hide pagination, disable grab.
   * When they don't: restore everything.
   */
  OmiSwiperSlider.prototype._checkOverflow = function () {
    if (!this.swiper) return;

    var sw            = this.swiper;
    var containerWidth = sw.el.clientWidth;
    var slides        = sw.slides;

    /* Sum rendered slide widths + gaps */
    var totalWidth = 0;
    for (var i = 0; i < slides.length; i++) {
      totalWidth += slides[i].offsetWidth;
    }
    totalWidth += sw.params.spaceBetween * Math.max(0, slides.length - 1);

    var fits = totalWidth <= containerWidth;

    if (fits) {
      sw.params.slidesOffsetAfter = 0;
      this.section.classList.add('omi-swiper--no-overflow');
    } else {
      /* Re-read the correct offset for the current breakpoint */
      sw.params.slidesOffsetAfter = sw.currentBreakpoint
        ? (sw.originalParams.breakpoints[sw.currentBreakpoint] || {}).slidesOffsetAfter || sw.originalParams.slidesOffsetAfter
        : sw.originalParams.slidesOffsetAfter;
      this.section.classList.remove('omi-swiper--no-overflow');
    }
  };

  /**
   * Announce the current slide to assistive technology via a live region.
   * Debounced to avoid spamming rapid changes.
   */
  OmiSwiperSlider.prototype._announceSlide = function () {
    if (!this.liveRegion || !this.swiper) return;

    var self = this;
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(function () {
      var index    = self.swiper.activeIndex + 1;
      var total    = self.swiper.slides.length;
      var slideEl  = self.swiper.slides[self.swiper.activeIndex];
      var label    = slideEl ? slideEl.getAttribute('aria-label') : '';
      self.liveRegion.textContent = label || ('Slide ' + index + ' of ' + total);
    }, 150);
  };

  /**
   * Tear down a Swiper instance cleanly.
   */
  OmiSwiperSlider.prototype.destroy = function () {
    clearTimeout(this._announceTimer);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  };

  /* ── Bootstrap helpers ─────────────────────────────────── */

  function initAll() {
    var sections = document.querySelectorAll('[data-omi-swiper-slider]');
    for (var i = 0; i < sections.length; i++) {
      var id = sections[i].dataset.sectionId;
      if (id && !instances[id]) {
        instances[id] = new OmiSwiperSlider(sections[i]);
      }
    }
  }

  /**
   * Poll for the global Swiper constructor.
   * Stops after ~5 s to avoid infinite loops.
   */
  function waitForSwiper(callback, elapsed) {
    elapsed = elapsed || 0;
    if (typeof Swiper !== 'undefined') {
      callback();
    } else if (elapsed < 5000) {
      setTimeout(function () {
        waitForSwiper(callback, elapsed + 100);
      }, 100);
    } else {
      console.warn('[OmiSwiperSlider] Swiper library not found after 5 s. Slider will not initialise.');
    }
  }

  /* ── Init on page load ─────────────────────────────────── */
  function boot() {
    waitForSwiper(initAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ── Shopify Theme Editor support ──────────────────────── */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-omi-swiper-slider]');
    if (!el) return;
    waitForSwiper(function () {
      var id = el.dataset.sectionId;
      instances[id] = new OmiSwiperSlider(el);
    });
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-omi-swiper-slider]');
    if (!el) return;
    var id = el.dataset.sectionId;
    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  });
})();